import { Exchange, Account, AccountUser } from '../../types/exchange'

// Determine API base URL at runtime
// In production (Cloudflare Pages), always use relative /api path (handled by Cloudflare Functions)
// In development, use VITE_API_BASE_URL if set, otherwise default to /api (proxy)
const getApiBase = (): string => {
  // Always check runtime hostname first - this is the source of truth
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    const protocol = window.location.protocol
    
    // If we're on HTTPS (production), ALWAYS use /api (ignore any env vars)
    // This prevents CORS issues when production tries to access localhost
    if (protocol === 'https:') {
      return '/api'
    }
    
    // If we're on a production domain, ALWAYS use /api (ignore any env vars)
    if (hostname === 'open1031.pages.dev' || 
        hostname.endsWith('.pages.dev') ||
        hostname === 'open1031.ai' ||
        hostname.endsWith('.open1031.ai') ||
        hostname.includes('cloudflarepages.dev')) {
      return '/api'
    }
    
    // NEVER use localhost URLs in production - they cause CORS issues
    if (import.meta.env.PROD) {
      return '/api'
    }
    
    // If we're on localhost in dev, use /api (Vite proxy)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return '/api'
    }
  }
  
  // In development only, check if VITE_API_BASE_URL is set and not localhost
  // But only if we're not in production mode
  if (!import.meta.env.PROD) {
    const envUrl = import.meta.env.VITE_API_BASE_URL
    // NEVER use localhost URLs - they cause CORS issues
    if (envUrl && 
        !envUrl.includes('localhost') && 
        !envUrl.includes('127.0.0.1') &&
        !envUrl.startsWith('http://localhost') &&
        !envUrl.startsWith('https://localhost')) {
      return envUrl
    }
  }
  
  // Default to relative /api (will use Vite proxy in dev, Cloudflare Functions in prod)
  return '/api'
}

export const exchangeApi = {
  // Get all exchanges for the current user's accounts
  async getExchanges(): Promise<Exchange[]> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${getApiBase()}/exchanges`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch exchanges')
    }
    return response.json()
  },

  // Get a specific exchange
  async getExchange(id: string): Promise<Exchange> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${getApiBase()}/exchanges/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch exchange')
    }
    return response.json()
  },

  // Create a new exchange
  async createExchange(data: { name: string; accountId?: string }): Promise<Exchange> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${getApiBase()}/exchanges`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create exchange')
    }
    return response.json()
  },

  // Update an exchange
  async updateExchange(id: string, data: Partial<Exchange>): Promise<Exchange> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${getApiBase()}/exchanges/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      throw new Error('Failed to update exchange')
    }
    return response.json()
  },

  // Get user's accounts
  async getAccounts(): Promise<Account[]> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${getApiBase()}/accounts`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch accounts')
    }
    return response.json()
  },

  // Create a new account
  async createAccount(data: { name: string }): Promise<Account> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${getApiBase()}/accounts`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create account')
    }
    return response.json()
  },

  // Get account users
  async getAccountUsers(accountId: string): Promise<AccountUser[]> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${getApiBase()}/accounts/${accountId}/users`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch account users')
    }
    return response.json()
  },

  // Add user to account
  async addAccountUser(accountId: string, data: { userId: string; role: AccountUser['role']; allowedExchanges?: string[] }): Promise<AccountUser> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${getApiBase()}/accounts/${accountId}/users`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to add user to account')
    }
    return response.json()
  },

  // Exchange permission endpoints
  async getExchangeUsers(exchangeId: string): Promise<any[]> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${getApiBase()}/exchanges/${exchangeId}/users`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch exchange users')
    }
    return response.json()
  },

  async addExchangeUser(exchangeId: string, data: {
    email?: string
    userId?: string
    permissionLevel: string
    userType: string
    featurePermissions?: any
    customTypeName?: string
  }): Promise<any> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${getApiBase()}/exchanges/${exchangeId}/users`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to add user to exchange')
    }
    return response.json()
  },

  async updateExchangeUserPermission(
    exchangeId: string,
    userId: string,
    updates: Partial<any>
  ): Promise<any> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${getApiBase()}/exchanges/${exchangeId}/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    })
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update permission')
    }
    return response.json()
  },

  async removeExchangeUserPermission(exchangeId: string, userId: string): Promise<void> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${getApiBase()}/exchanges/${exchangeId}/users/${userId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to remove permission')
    }
  },

  // User search for invites
  async searchUsers(query: string, accountId?: string): Promise<any[]> {
    const token = localStorage.getItem('auth_token')
    const params = new URLSearchParams({ q: query })
    if (accountId) params.append('accountId', accountId)
    const response = await fetch(`${getApiBase()}/users/search?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      throw new Error('Failed to search users')
    }
    return response.json()
  },

  // Invite management
  async getInvites(): Promise<any[]> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${getApiBase()}/invites`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch invites')
    }
    return response.json()
  },

  async acceptInvite(inviteId: string): Promise<any> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${getApiBase()}/invites/${inviteId}/accept`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to accept invite')
    }
    return response.json()
  },
}

